// ./src/components/features/Travel/ActiveTrackingBanner.tsx
//
// Polished GPS tracking banner with gradient background, large readable stats,
// and inline Pause/Resume control. Displayed below the header on all pages
// when tracking is active or paused.
//
// Reads distance/speed preferences from UserProfileContext.
// Distance stored in km internally — converted to user's distanceIn preference
// using kmToMiles from formatHelpers.

import React, { useMemo, useCallback } from "react";
import {
  Group,
  Text,
  Box,
  ActionIcon,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { kmToMiles } from "@/utils/formatHelpers";

import { type TrackingSessionState_UI } from "@/hooks/useTrackingSession";

import { useUserProfileContext } from "@/contexts/UserProfileContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface ActiveTrackingBannerProps {
  /** Client-side tracking session state from ActiveTravelContext */
  session: TrackingSessionState_UI;
  /** Called when the banner body is tapped (opens drawer) */
  onTap: () => void;
  /** Called when the inline Pause button is pressed */
  onPause: () => void;
  /** Called when the inline Resume button is pressed */
  onResume: () => void;
  /** Whether pause is in progress */
  isPausing?: boolean;
  /** Whether resume is in progress */
  isResuming?: boolean;
}

// ============================================
// Helpers
// ============================================

type BannerState = "active" | "paused" | "restoring";

const getBannerState = (
  status: TrackingSessionState_UI["status"]
): BannerState => {
  if (status === "paused") return "paused";
  if (status === "restoring") return "restoring";
  return "active";
};

const formatDuration = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatTrackingDistance = (km: number, useMiles: boolean): string => {
  const value = useMiles ? kmToMiles(km) : km;
  return value.toFixed(1);
};

const formatTrackingSpeed = (kmh: number | null, useMiles: boolean): string => {
  if (kmh == null) return "\u2014";
  const value = useMiles ? kmToMiles(kmh) : kmh;
  return String(Math.round(value));
};

const formatHeading = (degrees: number | null): string | null => {
  if (degrees == null) return null;
  const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

type GpsSignal = "good" | "fair" | "poor" | "none";

const getGpsSignal = (accuracy: number | null): GpsSignal => {
  if (accuracy == null) return "none";
  if (accuracy <= 10) return "good";
  if (accuracy <= 30) return "fair";
  return "poor";
};

const GPS_SIGNAL_COLORS: Record<GpsSignal, string> = {
  good: "rgba(255,255,255,0.9)",
  fair: "rgba(255,220,100,0.9)",
  poor: "rgba(255,130,130,0.9)",
  none: "rgba(255,255,255,0.4)",
};

// ============================================
// Gradient configs per state
// ============================================

const BANNER_GRADIENTS: Record<BannerState, { from: string; to: string }> = {
  active: { from: "#1a9d5c", to: "#15803d" },
  paused: { from: "#d97706", to: "#b45309" },
  restoring: { from: "#6b7280", to: "#4b5563" },
};

// ============================================
// Keyframes
// ============================================

const TrackingKeyframes: React.FC = () => (
  <style>
    {`
      @keyframes trackingDotPulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
    `}
  </style>
);

// ============================================
// Stat Cell
// ============================================

interface StatCellProps {
  label: string;
  value: string;
  unit?: string;
  large?: boolean;
}

const StatCell: React.FC<StatCellProps> = ({ label, value, unit, large }) => (
  <Box style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
    <Text
      size="xs"
      tt="uppercase"
      fw={600}
      style={{ color: "rgba(255,255,255,0.6)", letterSpacing: 0.5 }}
      lh={1}
    >
      {label}
    </Text>
    <Group gap={2} justify="center" align="baseline" mt={2}>
      <Text
        fw={700}
        style={{
          color: "white",
          fontVariantNumeric: "tabular-nums",
          fontSize: large ? "1.4rem" : "1.1rem",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Text>
      {unit && (
        <Text size="xs" style={{ color: "rgba(255,255,255,0.65)" }}>
          {unit}
        </Text>
      )}
    </Group>
  </Box>
);

// ============================================
// Vertical divider (white, translucent)
// ============================================

const StatDivider: React.FC = () => (
  <Box
    style={{
      width: 1,
      height: 28,
      backgroundColor: "rgba(255,255,255,0.15)",
      flexShrink: 0,
    }}
  />
);

// ============================================
// Component
// ============================================

const ActiveTrackingBannerComponent: React.FC<ActiveTrackingBannerProps> = ({
  session,
  onTap,
  onPause,
  onResume,
  isPausing = false,
  isResuming = false,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["travels", "common"]);
  const { distanceUnit } = useUserProfileContext();

  const useMiles = distanceUnit === "mi";
  const speedUnit = useMiles ? "mph" : "km/h";

  const bannerState = useMemo(() => getBannerState(session.status), [session.status]);
  const isPaused = bannerState === "paused";
  const isActive = bannerState === "active";
  const gradient = BANNER_GRADIENTS[bannerState];

  const gpsSignal = useMemo(() => getGpsSignal(session.gpsAccuracy), [session.gpsAccuracy]);

  const distanceStr = useMemo(
    () => formatTrackingDistance(session.gpsDistance, useMiles),
    [session.gpsDistance, useMiles]
  );
  const durationStr = useMemo(
    () => formatDuration(session.activeDuration),
    [session.activeDuration]
  );
  const speedStr = useMemo(
    () => formatTrackingSpeed(session.currentSpeed, useMiles),
    [session.currentSpeed, useMiles]
  );
  const headingStr = useMemo(
    () => formatHeading(session.currentHeading),
    [session.currentHeading]
  );

  const handleBodyTap = useCallback(() => { onTap(); }, [onTap]);

  const handlePauseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onPause();
  }, [onPause]);

  const handleResumeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onResume();
  }, [onResume]);

  const statusLabel = isPaused
    ? t("travels:tracking.paused", { defaultValue: "PAUSED" })
    : t("travels:tracking.live", { defaultValue: "LIVE TRACKING" });

  return (
    <>
      <Box
        onClick={handleBodyTap}
        px="md"
        py="sm"
        style={{
          background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
          borderRadius: theme.radius.lg,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          userSelect: "none",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative circle (subtle, matching vehicle card) */}
        <Box
          style={{
            position: "absolute",
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.06)",
            pointerEvents: "none",
          }}
        />

        {/* Top row: status + GPS signal + pause/resume */}
        <Group justify="space-between" wrap="nowrap" mb={8}>
          <Group gap={8} wrap="nowrap">
            {/* Pulsing dot */}
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "white",
                animation: isActive
                  ? "trackingDotPulse 1.5s ease-in-out infinite"
                  : undefined,
                opacity: isPaused ? 0.5 : 1,
                flexShrink: 0,
              }}
            />
            <Text
              size="xs"
              fw={700}
              tt="uppercase"
              style={{
                color: "rgba(255,255,255,0.85)",
                letterSpacing: 1,
              }}
            >
              {statusLabel}
            </Text>

            {/* GPS signal */}
            {isActive && (
              <Tooltip
                label={t("travels:tracking.gpsAccuracy", {
                  defaultValue: "GPS accuracy: {{meters}}m",
                  meters: session.gpsAccuracy != null ? Math.round(session.gpsAccuracy) : "\u2014",
                })}
                position="bottom"
                withArrow
                openDelay={500}
              >
                <Box style={{ display: "flex", alignItems: "center" }}>
                  <AppIcon
                    iconName={gpsSignal === "poor" || gpsSignal === "none" ? "AntennaOff" : "Antenna"}
                    size={14}
                    color={GPS_SIGNAL_COLORS[gpsSignal]}
                  />
                </Box>
              </Tooltip>
            )}
          </Group>

          {/* Pause button — 48px for comfortable mobile tap */}
          {isActive && (
            <Tooltip
              label={t("travels:tracking.actions.pause", { defaultValue: "Pause tracking" })}
              position="left"
              withArrow
              openDelay={400}
            >
              <ActionIcon
                variant="transparent"
                size={48}
                radius="md"
                onClick={handlePauseClick}
                loading={isPausing}
                loaderProps={{ size: 20, color: "white" }}
                style={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "1px solid rgba(255,255,255,0.25)",
                  flexShrink: 0,
                }}
              >
                <AppIcon iconName="PlayerPause" size={24} color="white" />
              </ActionIcon>
            </Tooltip>
          )}

          {/* Resume button — 48px, brighter to draw attention */}
          {isPaused && (
            <Tooltip
              label={t("travels:tracking.actions.resume", { defaultValue: "Resume tracking" })}
              position="left"
              withArrow
              openDelay={400}
            >
              <ActionIcon
                variant="transparent"
                size={48}
                radius="md"
                onClick={handleResumeClick}
                loading={isResuming}
                loaderProps={{ size: 20, color: "white" }}
                style={{
                  backgroundColor: "rgba(255,255,255,0.25)",
                  border: "1px solid rgba(255,255,255,0.4)",
                  flexShrink: 0,
                }}
              >
                <AppIcon iconName="PlayerPlay" size={24} color="white" />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {/* Stats row */}
        <Group
          wrap="nowrap"
          py={6}
          gap={0}
          style={{
            borderTop: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          <StatCell
            label={t("travels:tracking.stats.distance", { defaultValue: "Distance" })}
            value={distanceStr}
            unit={distanceUnit}
            large
          />

          <StatDivider />

          <StatCell
            label={t("travels:tracking.stats.duration", { defaultValue: "Duration" })}
            value={durationStr}
          />

          <StatDivider />

          <StatCell
            label={t("travels:tracking.stats.speed", { defaultValue: "Speed" })}
            value={speedStr}
            unit={speedUnit}
          />

          {headingStr && (
            <>
              <StatDivider />
              <StatCell
                label={t("travels:tracking.stats.heading", { defaultValue: "Heading" })}
                value={headingStr}
              />
            </>
          )}
        </Group>

        {/* Buffered points indicator — subtle bottom-right corner */}
        {session.pointsBuffered > 0 && (
          <Tooltip
            label={t("travels:tracking.pointsBuffered", {
              defaultValue: "{{count}} points pending upload",
              count: session.pointsBuffered,
            })}
            position="top"
            withArrow
            openDelay={500}
          >
            <Group
              gap={3}
              wrap="nowrap"
              style={{
                position: "absolute",
                bottom: 4,
                right: 10,
                opacity: 0.45,
              }}
            >
              <AppIcon iconName="CloudUpload" size={10} color="white" />
              <Text style={{ color: "white", fontSize: 10 }}>
                {session.pointsBuffered}
              </Text>
            </Group>
          </Tooltip>
        )}
      </Box>

      <TrackingKeyframes />
    </>
  );
};

export const ActiveTrackingBanner = React.memo(ActiveTrackingBannerComponent);